import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { pullContactState } from "./store";
import Button from "../../Components/common/Button";
import { ReactComponent as PullContactsIcon } from "./svg/pullContacts.svg";
import { ReactComponent as ContactsFoundIcon } from "./svg/contactsFound.svg";
import PasteInput from "./PasteInput";
import ListSequence from "./ListSequence";
import userDetail from "Stores/userDetail";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import Utils from "Utils/utils";

function getTextAndButton() {
  let description = "";
  let buttonText = "Extract";

  const { pullContactsCount } = pullContactState;
  const { snHardLimit, advancedSnImportEnabled } = userDetail.userFeatureFlag;

  let smallNotes = `${Utils.formatNumberToStringInUSFormat(
    pullContactsCount
  )} contacts will be extracted`;

  const enterpriseLimit = snHardLimit || 25000;
  const enterpriseLimitInString =
    Utils.formatNumberToStringInUSFormat(enterpriseLimit);

  if (advancedSnImportEnabled) {
    if (pullContactsCount <= enterpriseLimit) {
      description =
        "All the contacts from this search will be extracted to Salesgear.";
    } else if (
      pullContactsCount > enterpriseLimit &&
      pullContactsCount <= 50000
    ) {
      description = `Your plan allows up to ${enterpriseLimitInString} contacts per search. We'll extract the first ${enterpriseLimitInString} from this result.`;
      smallNotes = `${enterpriseLimitInString} out of ${Utils.formatNumberToStringInUSFormat(
        pullContactsCount
      )} contacts will be extracted`;
      buttonText = `Extract ${enterpriseLimitInString} Contacts now`;
    } else {
      description = `Your plan supports extracting up to <b>${enterpriseLimitInString}</b>, but only from Sales Navigator searches with <b>50,000</b> results or fewer.
Please narrow your Sales Navigator search to below <b>50,000</b> contacts and try again.`;
    }
  } else if (pullContactsCount >= 2500) {
    description = `You can extract up to 2,500 contacts from this Sales Navigator search. Upgrade to unlock higher extraction limits per search.`;
    buttonText = "Extract 2,500 Contacts now";
    smallNotes = `2,500 out of ${Utils.formatNumberToStringInUSFormat(
      pullContactsCount
    )} contacts will be extracted`;
  } else {
    description = ` All ${Utils.formatNumberToStringInUSFormat(
      pullContactsCount
    )} contacts will be pulled from Sales Navigator into Salesgear.`;
  }

  return {
    description,
    buttonText,
    smallNotes
  };
}

const PullContacts = observer(() => {
  const history = useHistory();
  const handleSearch = () => {
    pullContactState.setSearchLoading(true);
    pullContactState.lookupContacts();
  };

  const handleExtract = () => {
    pullContactState.setExtractBtnLoading(true);
    pullContactState.extractContacts(history);
  };

  const handleShowAddToSeq = () => {
    pullContactState.setShowListSeq(true);
  };

  const closeCbk = () => {
    pullContactState.setShowPopup(false);
    pullContactState.resetFunc();
  };

  const getSequenceList = async (pageNo = 1) => {
    const payload = {
      pageNo: pageNo || 1,
      limit: 500,
      filters: {
        memberIds: [userDetail?.userInfo?.memberId]
      }
    };
    let seqObjResponse = await pullContactState.getSequenceData(payload);
    const { totalPages = 0, currentPage = 0 } = seqObjResponse || {};
    if (totalPages > 0 && currentPage < totalPages) {
      getSequenceList(currentPage + 1);
    }
    return true;
  };

  useEffect(() => {
    pullContactState.getContactList();
    getSequenceList();
  }, []);

  const { pullContactsCount } = pullContactState;

  const { description, buttonText, smallNotes } = getTextAndButton();

  const enterpriseLimit = userDetail.userFeatureFlag.snHardLimit || 25000;

  return (
    pullContactState.showPopup && (
      <>
        <div className="pullContactsMask" onClick={closeCbk} />
        <div className="pullContactsWrapper">
          <div className="card">
            <i className="material-icons closeIcon" onClick={closeCbk}>
              close
            </i>
            <PullContactsIcon />
            <p className="pullContactsText">
              Pull contacts from LinkedIn sales navigator
            </p>
            <PasteInput />
            {pullContactState.extractBtn ? (
              <>
                <div
                  className={`extractContactsWrapper ${
                    pullContactState.showListSeq ? "showListSeq" : ""
                  }`}
                >
                  <div className="extractTextWithNotes">
                    <ContactsFoundIcon />
                    <div className="flexWrapperWithIcon">
                      {pullContactState.showListSeq ? (
                        <p className="extractContactsText">{smallNotes}</p>
                      ) : (
                        <p className="extractContactsText">
                          <strong>{`${Utils.formatNumberToStringInUSFormat(
                            pullContactsCount
                          )} ${
                            pullContactsCount > 1 ? "contacts" : "contact"
                          } `}</strong>{" "}
                          <span>{` found`}</span>
                        </p>
                      )}
                      {!pullContactState.showListSeq && (
                        <div className="flexWrapper">
                          <p
                            className="smallNotesText"
                            dangerouslySetInnerHTML={{ __html: description }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {pullContactState.showListSeq && (
                    <div className="extractBtnWrapper">
                      <div className="listSequenceContainer">
                        <ListSequence />
                      </div>
                    </div>
                  )}
                </div>
                {userDetail.userFeatureFlag.advancedSnImportEnabled &&
                pullContactsCount > 50000 ? (
                  <Button
                    btnText="Adjust Search in Sales Navigator"
                    onClick={() => {
                      window.open(pullContactState.pasteInputValue, "_blank");
                    }}
                    className="extractBtn"
                  />
                ) : (
                  <div className="buttonWrapper">
                    {((pullContactsCount > 2500 &&
                      !userDetail.userFeatureFlag.advancedSnImportEnabled) ||
                      (pullContactsCount > enterpriseLimit &&
                        userDetail.userFeatureFlag
                          .advancedSnImportEnabled)) && (
                      <>
                        <Button
                          btnText={`Unlock All ${Utils.formatNumberToStringInUSFormat(
                            pullContactsCount
                          )} Contacts`}
                          onClick={() => {
                            window.Intercom("show");
                            pullContactState.resetFunc();
                          }}
                          className="extractAll"
                        />
                      </>
                    )}
                    <Button
                      btnText={buttonText}
                      onClick={
                        !pullContactState.showListSeq
                          ? handleShowAddToSeq
                          : handleExtract
                      }
                      className={`extractBtn ${
                        pullContactState.disableExtractBtn
                          ? "disableExtractBtn"
                          : ""
                      } ${
                        pullContactState?.extractBtnLoading ? "loading" : ""
                      }`}
                      loader={pullContactState.extractBtnLoading}
                    />
                  </div>
                )}
              </>
            ) : (
              <Button
                btnText={"Search"}
                onClick={handleSearch}
                className={`searchWrapper ${
                  pullContactState?.searchLoading ? "loading" : ""
                }`}
                loader={pullContactState.searchLoading}
              />
            )}
          </div>
        </div>
      </>
    )
  );
});

export { PullContacts };
export default PullContacts;
